import { Fragment, useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  D,
  fmt,
  fmtDateTime,
  fmtUsd,
  NETWORKS,
  SOF_CHECKLIST,
  SOF_DOC_KINDS,
  SOF_EXPECTED_BANDS,
  SOF_INCOME_BANDS,
  SOF_SOURCES,
  TICKET_CATEGORIES,
  ticketCategoryLabel,
  type ComplianceState,
  type Deposit,
  type KycKind,
  type KycSubmission,
  type Network,
  type SofDocKind,
  type SofDocument,
  type SofSubmission,
  type Ticket,
  type TicketDetail,
  type TicketStatus,
  type Withdrawal,
} from '@platform/shared';
import { ClientCell, clientLabel } from '../components/ClientCell';
import { DateField } from '../components/DateRange';
import { cx, Empty, Modal, Spinner } from '../components/ui';
import { api, ApiError, getAccessToken, refreshAccessToken } from '../lib/api';
import { API_URL } from '../lib/config';
import { useQuote } from '../stores/market';
import { WalletCards } from './AdminWallets';

// ---------- shared (same conventions as AdminPage) ----------

function useLoad<T>(path: string | null, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const reload = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try {
      setData(await api.get<T>(path));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    void reload();
  }, [reload, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, error, loading, reload };
}

const th = 'px-3 py-2 text-left text-[11px] font-medium text-muted whitespace-nowrap';
const td = 'px-3 py-2 whitespace-nowrap';
const input = 'h-9 rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const textarea = 'rounded-lg border border-line bg-bg p-2 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const btn = 'h-8 rounded-md border border-line-strong px-3 text-xs font-semibold text-fg disabled:opacity-50';
const btnPrimary = 'h-8 rounded-md bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50';
const btnDanger = 'h-8 rounded-md bg-btn-short px-4 text-xs font-semibold text-white disabled:opacity-50';

function Table({ head, children, minWidth = 900 }: { head: string[]; children: ReactNode; minWidth?: number }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-panel">
      <table className="w-full border-collapse text-xs" style={{ minWidth }}>
        <thead className="border-b border-line">
          <tr>
            {head.map((h, i) => (
              <th key={`${h}-${i}`} className={th}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Err({ msg }: { msg: string | null }) {
  return msg ? (
    <p className="m-0 text-xs text-down" role="alert">
      {msg}
    </p>
  ) : null;
}

/** Status filter button row (same pattern as the Webhooks tab). */
function Filter<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { id: T; label: string }[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o.id || 'all'} type="button" aria-pressed={value === o.id} onClick={() => onChange(o.id)} className={cx(btn, value === o.id && 'bg-raised')}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

const when = (x: string | null | undefined) => fmtDateTime(x);
const errMsg = (e: unknown, fallback = 'Failed') => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : fallback);
const short = (s: string | null | undefined, head = 8, tail = 6) => (!s ? '—' : s.length <= head + tail + 1 ? s : `${s.slice(0, head)}…${s.slice(-tail)}`);
const bytes = (n: number) => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

const statusCls = (s: string) =>
  ['approved', 'paid', 'credited', 'resolved'].includes(s) ? 'text-up' : ['rejected'].includes(s) ? 'text-down' : ['cancelled'].includes(s) ? 'text-muted' : 'text-warn';

/** Authenticated file download (the API needs the Bearer header, so a plain <a href> won't do). */
async function fetchBlob(path: string, retried = false): Promise<Blob> {
  const token = getAccessToken();
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include' });
  } catch {
    throw new Error('Network error — check your connection');
  }
  if (res.status === 401 && !retried && (await refreshAccessToken())) return fetchBlob(path, true);
  if (!res.ok) {
    const j = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new Error(j?.message ?? `Download failed (${res.status})`);
  }
  return res.blob();
}

async function downloadFile(path: string, filename: string) {
  const url = URL.createObjectURL(await fetchBlob(path));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// ---------- KYC ----------

type KycFilter = 'pending' | 'approved' | 'partial' | 'rejected';

interface KycRow {
  id: string;
  scope: KycSubmission['scope'];
  docType: string;
  status: KycSubmission['status'];
  rejectReason: string | null;
  rejectedKinds: KycKind[];
  createdAt: string;
  reviewedAt: string | null;
  userId: string;
  email: string;
  /** Profile name, else the CRM name. */
  name: string | null;
  externalClientId: string | null;
  country: string | null;
}

interface KycDetail {
  submission: KycSubmission;
  user: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    crm_name: string | null;
    external_client_id: string | null;
    dob: string | null;
    country: string | null;
    address_line: string | null;
    city: string | null;
    postal_code: string | null;
    phone_cc: string | null;
    phone: string | null;
  };
  documents: { id: string; kind: KycKind; status: string; filename: string; mime: string; size: number }[];
}

const KIND_LABEL: Record<KycKind, string> = { id_front: 'ID front', id_back: 'ID back', selfie: 'Selfie with ID', poa: 'Proof of address' };
const DOC_TYPE_LABEL: Record<string, string> = { passport: 'Passport', id_card: 'ID card', drivers_license: "Driver's license" };

function DocPreview({ doc, path, label }: { doc: { id: string; status: string; filename: string; mime: string; size: number; kind?: string }; path?: string; label?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    let made: string | null = null;
    fetchBlob(path ?? `/api/admin/kyc/documents/${doc.id}/file`)
      .then((b) => {
        made = URL.createObjectURL(b);
        if (alive) setUrl(made);
        else URL.revokeObjectURL(made);
      })
      .catch((e) => alive && setErr(errMsg(e, 'Could not load file')));
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [doc.id, path]);
  const isImage = doc.mime.startsWith('image/');
  const title = label ?? KIND_LABEL[doc.kind as KycKind] ?? doc.kind ?? 'Document';
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line p-2.5 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">{title}</span>
        <span className={cx('text-[11px]', statusCls(doc.status))}>{doc.status}</span>
      </div>
      <div className="grid h-44 place-items-center overflow-hidden rounded-md bg-raised">
        {err ? (
          <span className="px-2 text-center text-down">{err}</span>
        ) : !url ? (
          <Spinner />
        ) : isImage ? (
          <a href={url} target="_blank" rel="noreferrer" className="block size-full" title="Open full size">
            <img src={url} alt={title} className="size-full object-contain" />
          </a>
        ) : (
          <button type="button" className={btn} onClick={() => window.open(url, '_blank', 'noopener')}>
            Open PDF
          </button>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 text-[11px] text-muted">
        <span className="truncate" title={doc.filename}>
          {doc.filename}
        </span>
        <span className="num shrink-0">{bytes(doc.size)}</span>
      </div>
    </div>
  );
}

function KycReview({ id, onClose, onDone }: { id: string; onClose: () => void; onDone: () => void }) {
  const { data, error } = useLoad<KycDetail>(`/api/admin/kyc/${id}`);
  const [reason, setReason] = useState('');
  const [kinds, setKinds] = useState<KycKind[]>([]);
  const [idExpiresOn, setIdExpiresOn] = useState('');
  const [poaIssuedOn, setPoaIssuedOn] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const decide = async (body: object) => {
    setBusy(true);
    setMsg(null);
    try {
      await api.post(`/api/admin/kyc/${id}/review`, body);
      onDone();
      onClose();
    } catch (e) {
      setMsg(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const u = data?.user;
  const s = data?.submission;
  const poaOnly = s?.scope === 'poa_update';
  const datesOk = !!poaIssuedOn && (poaOnly || !!idExpiresOn);
  const name = u ? [u.first_name, u.last_name].filter(Boolean).join(' ') || '—' : '';
  const address = u ? [u.address_line, u.city, u.postal_code].filter(Boolean).join(', ') || '—' : '';
  return (
    <Modal open onClose={onClose} title={u ? `KYC review · ${clientLabel({ name: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.crm_name, email: u.email, crmId: u.external_client_id })}` : 'KYC review'} width={760}>
      <Err msg={error} />
      {!data || !u || !s ? (
        !error && <Spinner />
      ) : (
        <div className="flex max-h-[75vh] flex-col gap-4 overflow-y-auto">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded bg-raised px-2 py-1">{poaOnly ? 'Proof of address update' : (DOC_TYPE_LABEL[s.docType] ?? s.docType)}</span>
            <span className={cx('rounded bg-raised px-2 py-1 font-semibold', statusCls(s.status))}>{s.status}</span>
            <span className="text-muted">Submitted {when(s.createdAt)}</span>
            {s.reviewedAt && <span className="text-muted">· Reviewed {when(s.reviewedAt)}</span>}
          </div>
          {s.status === 'rejected' && s.rejectReason && (
            <p className="m-0 rounded-lg bg-down-bg px-3 py-2 text-xs text-down">
              Rejected: {s.rejectReason}
              {s.rejectedKinds.length > 0 && ` (replace: ${s.rejectedKinds.map((k) => KIND_LABEL[k]).join(', ')})`}
            </p>
          )}
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-lg border border-line p-3 text-xs">
            <span className="text-muted">Name</span>
            <span className="font-semibold">{name}</span>
            <span className="text-muted">Email</span>
            <span>{u.email}</span>
            <span className="text-muted">Date of birth</span>
            <span className="num">{u.dob ?? '—'}</span>
            <span className="text-muted">Country</span>
            <span>{u.country ?? '—'}</span>
            <span className="text-muted">Address</span>
            <span>{address}</span>
            <span className="text-muted">Phone</span>
            <span className="num">{u.phone ? `${u.phone_cc ?? ''} ${u.phone}`.trim() : '—'}</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {data.documents.map((d) => (
              <DocPreview key={d.id} doc={d} />
            ))}
          </div>
          {!data.documents.length && <p className="m-0 text-xs text-muted">No documents in this submission</p>}
          {s.status === 'pending' && (
            <>
              <div className="flex flex-col gap-2.5 rounded-lg border border-line p-3">
                <h3 className="m-0 text-sm font-semibold">Approve</h3>
                <div className="flex flex-wrap gap-3">
                  {!poaOnly && (
                    <label className="flex flex-col gap-1 text-[11px] text-muted">
                      ID document expires on
                      <DateField value={idExpiresOn} onChange={setIdExpiresOn} className={cx(input, 'w-[130px]')} />
                    </label>
                  )}
                  <label className="flex flex-col gap-1 text-[11px] text-muted">
                    Date on the proof of address
                    <DateField value={poaIssuedOn} onChange={setPoaIssuedOn} className={cx(input, 'w-[130px]')} />
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <button type="button" disabled={busy || !datesOk} className={btnPrimary} onClick={() => void decide({ decision: 'approve', idExpiresOn: poaOnly ? undefined : idExpiresOn, poaIssuedOn })}>
                    {busy ? <Spinner /> : 'Approve'}
                  </button>
                  <span className="text-[11.5px] text-muted">
                    {poaOnly
                      ? 'Accepts the new proof of address. It must be dated within the last 90 days.'
                      : 'Verifies the user and accepts all documents. The proof of address must be dated within the last 90 days.'}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2.5 rounded-lg border border-line p-3">
                <h3 className="m-0 text-sm font-semibold">Reject</h3>
                <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (shown to the user)" className={textarea} aria-label="Reject reason" />
                <div className="flex flex-col gap-1.5 text-xs">
                  <span className="text-muted">Documents the user must replace (none checked = all):</span>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                    {data.documents.map((d) => (
                      <label key={d.id} className="flex cursor-pointer items-center gap-1.5">
                        <input type="checkbox" checked={kinds.includes(d.kind)} onChange={(e) => setKinds((k) => (e.target.checked ? [...k, d.kind] : k.filter((x) => x !== d.kind)))} />
                        {KIND_LABEL[d.kind] ?? d.kind}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <button type="button" disabled={busy || !reason.trim()} className={btnDanger} onClick={() => void decide({ decision: 'reject', reason: reason.trim(), kinds })}>
                    {busy ? <Spinner /> : 'Reject'}
                  </button>
                </div>
              </div>
            </>
          )}
          <Err msg={msg} />
        </div>
      )}
    </Modal>
  );
}

export function KycTab() {
  const [status, setStatus] = useState<KycFilter>('pending');
  const [open, setOpen] = useState<string | null>(null);
  const { data, error, loading, reload } = useLoad<KycRow[]>(`/api/admin/kyc?status=${status}`);
  return (
    <div className="flex flex-col gap-3">
      <Filter
        value={status}
        onChange={setStatus}
        options={[
          { id: 'pending', label: 'Pending' },
          { id: 'approved', label: 'Approved' },
          { id: 'partial', label: 'Partly verified' },
          { id: 'rejected', label: 'Rejected' },
        ]}
      />
      <Err msg={error} />
      {status === 'pending' && <p className="m-0 text-xs text-muted">New clients are promised a decision within 24 hours. Reviews waiting 20 hours or more are shown in red.</p>}
      <Table head={['Submitted', 'Waiting', 'Client', 'Country', 'Document type', 'Status', '']}>
        {data?.map((k) => {
          const hours = Math.floor((Date.now() - Date.parse(k.createdAt)) / 3_600_000);
          return (
          <tr key={k.id} className="border-b border-line">
            <td className={cx(td, 'num text-muted')}>{when(k.createdAt)}</td>
            <td className={cx(td, 'num', k.status !== 'pending' ? 'text-muted' : hours >= 20 ? 'font-semibold text-down' : '')}>
              {k.status !== 'pending' ? '—' : hours < 1 ? '< 1 h' : hours < 48 ? `${hours} h` : `${Math.floor(hours / 24)} days`}
            </td>
            <td className={td}>
              <ClientCell name={k.name} email={k.email} crmId={k.externalClientId} />
            </td>
            <td className={td}>{k.country ?? '—'}</td>
            <td className={td}>{k.scope === 'poa_update' ? 'Proof of address update' : (DOC_TYPE_LABEL[k.docType] ?? k.docType)}</td>
            <td className={cx(td, statusCls(k.status))} title={k.rejectReason ?? undefined}>
              {k.status}
            </td>
            <td className={td}>
              <button type="button" className={btn} onClick={() => setOpen(k.id)}>
                {k.status === 'pending' ? 'Review' : 'View'}
              </button>
            </td>
          </tr>
          );
        })}
      </Table>
      {loading && !data && <Spinner />}
      {data && !data.length && <Empty>No {status} submissions</Empty>}
      {open && <KycReview id={open} onClose={() => setOpen(null)} onDone={() => void reload()} />}
    </div>
  );
}

// ---------- Source of funds ----------

interface SofRow extends SofSubmission {
  userId: string;
  email: string;
  /** Profile name, else the CRM name. */
  name: string | null;
  externalClientId: string | null;
  country: string | null;
  kycStatus: string;
  dueAt: string | null;
}

interface SofDetail {
  submission: SofSubmission;
  user: { id: string; email: string; first_name: string | null; last_name: string | null; crm_name: string | null; external_client_id: string | null; country: string | null; address_line: string | null; city: string | null; postal_code: string | null; kyc_status: string; id_expires_on: string | null };
  documents: (SofDocument & { mime?: string })[];
  compliance: ComplianceState;
}

const label = (list: { id: string; label: string }[], id: string | null) => list.find((x) => x.id === id)?.label ?? id ?? '—';
const sofKind = (k: SofDocKind) => label(SOF_DOC_KINDS, k);
const period = (d: SofDocument) => (d.periodFrom ? (d.periodFrom === d.periodTo ? d.periodFrom : `${d.periodFrom} – ${d.periodTo}`) : '');
const today = () => new Date().toISOString().slice(0, 10);

function SofReview({ id, onClose, onDone }: { id: string; onClose: () => void; onDone: () => void }) {
  const { data, error } = useLoad<SofDetail>(`/api/admin/sof/${id}`);
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [statementEnd, setStatementEnd] = useState('');
  const [alsoPoa, setAlsoPoa] = useState(false);
  const [poaDate, setPoaDate] = useState('');
  const [reason, setReason] = useState('');
  const [kinds, setKinds] = useState<SofDocKind[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const decide = async (body: object) => {
    setBusy(true);
    setMsg(null);
    try {
      await api.post(`/api/admin/sof/${id}/review`, body);
      onDone();
      onClose();
    } catch (e) {
      setMsg(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const u = data?.user;
  const s = data?.submission;
  const c = data?.compliance;
  const allTicked = SOF_CHECKLIST.every((x) => checks[x.id]);
  const kycOk = u?.kyc_status === 'approved';
  const presentKinds = [...new Set((data?.documents ?? []).map((d) => d.kind))];
  return (
    <Modal open onClose={onClose} title={u ? `Source of funds · ${clientLabel({ name: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.crm_name, email: u.email, crmId: u.external_client_id })}` : 'Source of funds'} width={820}>
      <Err msg={error} />
      {!data || !u || !s || !c ? (
        !error && <Spinner />
      ) : (
        <div className="flex max-h-[78vh] flex-col gap-4 overflow-y-auto">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className={cx('rounded bg-raised px-2 py-1 font-semibold', statusCls(s.status))}>{s.status}</span>
            <span className="text-muted">Submitted {when(s.createdAt)}</span>
            {s.reviewedAt && <span className="text-muted">· Reviewed {when(s.reviewedAt)}</span>}
            {s.validUntil && <span className="text-muted">· Valid until {s.validUntil}</span>}
            {c.sofRequired && c.sofDueAt && <span className="rounded bg-warn-bg px-2 py-1 text-warn">Grace ends {when(c.sofDueAt)}</span>}
          </div>
          {s.status === 'rejected' && s.rejectReason && (
            <p className="m-0 rounded-lg bg-down-bg px-3 py-2 text-xs text-down">
              Rejected: {s.rejectReason} (replace: {s.rejectedKinds.map(sofKind).join(', ')})
            </p>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="grid grid-cols-[auto_1fr] content-start gap-x-4 gap-y-1 rounded-lg border border-line p-3 text-xs">
              <span className="text-muted">Name</span>
              <span className="font-semibold">{[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}</span>
              <span className="text-muted">Address</span>
              <span>{[u.address_line, u.city, u.postal_code, u.country].filter(Boolean).join(', ') || '—'}</span>
              <span className="text-muted">KYC</span>
              <span className={statusCls(u.kyc_status)}>
                {u.kyc_status}
                {u.id_expires_on ? ` · ID to ${u.id_expires_on}` : ''}
                {c.poaIssuedOn ? ` · PoA ${c.poaIssuedOn}` : ''}
              </span>
              <span className="text-muted">Lifetime deposits</span>
              <span className="num">
                {fmtUsd(c.lifetimeDeposits)}
                {Number(c.pendingDeposits) > 0 ? ` + ${fmtUsd(c.pendingDeposits)} pending` : ''}
              </span>
            </div>
            <div className="grid grid-cols-[auto_1fr] content-start gap-x-4 gap-y-1 rounded-lg border border-line p-3 text-xs">
              <span className="text-muted">Source</span>
              <span className="font-semibold">{label(SOF_SOURCES, s.source)}</span>
              <span className="text-muted">Occupation</span>
              <span>
                {s.occupation}
                {s.employer ? ` · ${s.employer}` : ''}
              </span>
              <span className="text-muted">Annual income</span>
              <span>{label(SOF_INCOME_BANDS, s.incomeBand)}</span>
              <span className="text-muted">Expected 12 mo</span>
              <span>{label(SOF_EXPECTED_BANDS, s.expectedBand)}</span>
              {s.sourceDetails && (
                <>
                  <span className="text-muted">Details</span>
                  <span className="whitespace-pre-wrap">{s.sourceDetails}</span>
                </>
              )}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {data.documents.map((d) => (
              <DocPreview
                key={d.id}
                doc={{ ...d, mime: d.mime ?? (d.filename.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg') }}
                path={`/api/admin/sof/documents/${d.id}/file`}
                label={`${sofKind(d.kind)}${period(d) ? ` · ${period(d)}` : ''}`}
              />
            ))}
          </div>
          {s.status === 'pending' && (
            <>
              <div className="flex flex-col gap-2.5 rounded-lg border border-line p-3">
                <h3 className="m-0 text-sm font-semibold">Checklist</h3>
                {SOF_CHECKLIST.map((x) => (
                  <label key={x.id} className="flex cursor-pointer items-center gap-2 text-xs">
                    <input type="checkbox" checked={!!checks[x.id]} onChange={(e) => setChecks((v) => ({ ...v, [x.id]: e.target.checked }))} />
                    {x.label}
                  </label>
                ))}
                <div className="flex flex-wrap items-end gap-3">
                  <label className="flex flex-col gap-1 text-[11px] text-muted">
                    Latest statement ends on
                    <DateField max={today()} value={statementEnd} onChange={setStatementEnd} className={cx(input, 'w-[130px]')} />
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={alsoPoa} onChange={(e) => setAlsoPoa(e.target.checked)} />
                    Also accept as proof of address
                  </label>
                  {alsoPoa && (
                    <label className="flex flex-col gap-1 text-[11px] text-muted">
                      Statement date
                      <DateField max={today()} value={poaDate} onChange={setPoaDate} className={cx(input, 'w-[130px]')} />
                    </label>
                  )}
                </div>
                {!kycOk && <p className="m-0 text-xs text-warn">Approve the client's KYC first.</p>}
                <div>
                  <button
                    type="button"
                    disabled={busy || !allTicked || !kycOk || (alsoPoa && !poaDate)}
                    className={btnPrimary}
                    onClick={() => void decide({ decision: 'approve', checklist: checks, statementEnd: statementEnd || undefined, poaIssuedOn: alsoPoa ? poaDate : undefined })}
                  >
                    {busy ? <Spinner /> : 'Approve'}
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-2.5 rounded-lg border border-line p-3">
                <h3 className="m-0 text-sm font-semibold">Reject</h3>
                <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (shown to the client)" className={textarea} aria-label="Reject reason" />
                <div className="flex flex-col gap-1.5 text-xs">
                  <span className="text-muted">Documents the client must replace (none checked = bank statements):</span>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                    {presentKinds.map((k) => (
                      <label key={k} className="flex cursor-pointer items-center gap-1.5">
                        <input type="checkbox" checked={kinds.includes(k)} onChange={(e) => setKinds((v) => (e.target.checked ? [...v, k] : v.filter((x) => x !== k)))} />
                        {sofKind(k)}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <button type="button" disabled={busy || !reason.trim()} className={btnDanger} onClick={() => void decide({ decision: 'reject', reason: reason.trim(), kinds })}>
                    {busy ? <Spinner /> : 'Reject'}
                  </button>
                </div>
              </div>
            </>
          )}
          <Err msg={msg} />
        </div>
      )}
    </Modal>
  );
}

export function SofTab() {
  const [status, setStatus] = useState<KycFilter>('pending');
  const [open, setOpen] = useState<string | null>(null);
  const { data, error, loading, reload } = useLoad<SofRow[]>(`/api/admin/sof?status=${status}`);
  return (
    <div className="flex flex-col gap-3">
      <Filter
        value={status}
        onChange={setStatus}
        options={[
          { id: 'pending', label: 'Pending' },
          { id: 'approved', label: 'Approved' },
          { id: 'rejected', label: 'Rejected' },
        ]}
      />
      <Err msg={error} />
      <Table head={['Submitted', 'Client', 'Source', 'KYC', 'Grace ends', 'Status', '']}>
        {data?.map((r) => (
          <tr key={r.id} className="border-b border-line">
            <td className={cx(td, 'num text-muted')}>{when(r.createdAt)}</td>
            <td className={td}>
              <ClientCell name={r.name} email={r.email} crmId={r.externalClientId} />
            </td>
            <td className={td}>{label(SOF_SOURCES, r.source)}</td>
            <td className={cx(td, statusCls(r.kycStatus))}>{r.kycStatus}</td>
            <td className={cx(td, 'num text-muted')}>{r.dueAt ? when(r.dueAt) : '—'}</td>
            <td className={cx(td, statusCls(r.status))} title={r.rejectReason ?? undefined}>
              {r.status}
            </td>
            <td className={td}>
              <button type="button" className={btn} onClick={() => setOpen(r.id)}>
                {r.status === 'pending' ? 'Review' : 'View'}
              </button>
            </td>
          </tr>
        ))}
      </Table>
      {loading && !data && <Spinner />}
      {data && !data.length && <Empty>No {status} submissions</Empty>}
      {open && <SofReview id={open} onClose={() => setOpen(null)} onDone={() => void reload()} />}
    </div>
  );
}

// ---------- Withdrawals ----------

type WdFilter = 'pending' | 'under_review' | 'compliance' | 'approved' | 'paid' | 'rejected' | 'cancelled' | '';
type AdminWithdrawal = Withdrawal & {
  userId: string;
  email: string;
  /** Profile name, else the CRM name. */
  name: string | null;
  externalClientId: string | null;
  /** Wire / card: the full details to pay to, and whether the holder name matches the verified client. */
  payoutRows: [string, string][] | null;
  holderMatchesKyc: boolean | null;
  /** Compliance hold: internal reason and code; who put it on hold / started the review. */
  holdReason: string | null;
  holdCode: string | null;
  heldByName: string | null;
  reviewByName: string | null;
};
type WdMode = 'review' | 'hold' | 'release' | 'approve' | 'paid' | 'reject';
/** Which actions each status allows (WITHDRAWAL_REVIEW_PLAN.md §3.1). */
const WD_MODES: Record<string, WdMode[]> = {
  pending: ['review', 'approve', 'paid', 'hold', 'reject'],
  under_review: ['approve', 'paid', 'hold', 'reject'],
  compliance: ['release', 'reject'],
  approved: ['paid', 'hold', 'reject'],
};
const WD_MODE_LABEL: Record<WdMode, string> = { review: 'Start review', hold: 'Hold for compliance', release: 'Release', approve: 'Approve', paid: 'Mark as paid', reject: 'Reject' };
const CCY_SYM: Record<string, string> = { USD: '$', EUR: '€', GBP: '£' };
const wdMethod = (w: Withdrawal) => (w.method === 'crypto' ? `${w.coin ?? 'USDT'} · ${w.network}` : `${w.method === 'card' ? 'Card' : 'Wire'} ${w.currency}`);
/** Crypto amounts: USDT to 2 decimals, BTC / ETH to 8. */
const coinAmt = (v: string, coin: string | null) => `${fmt(v, !coin || coin === 'USDT' ? 2 : 8)} ${coin ?? 'USDT'}`;

function WithdrawalReview({ w, onClose, onDone }: { w: AdminWithdrawal; onClose: () => void; onDone: () => void }) {
  const fiat = w.method !== 'crypto';
  const modes = WD_MODES[w.status] ?? [];
  const [mode, setMode] = useState<WdMode>(modes.includes('paid') ? 'paid' : modes[0] ?? 'reject');
  const [holdCode, setHoldCode] = useState('');
  const [releaseTo, setReleaseTo] = useState<'under_review' | 'approved'>('under_review');
  const [walletOpen, setWalletOpen] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [reference, setReference] = useState('');
  // Entered in the coin, the client asked for an exact amount: start from it.
  const [amountSent, setAmountSent] = useState(() => (fiat ? (w.currency === 'USD' ? D.from(w.receive).toFixed(2) : (w.receiveEst ?? '')) : w.coinFixed ? D.from(w.receive).toString() : ''));
  // BTC / ETH: what sending the coin costs at the live mark, against what was held at request.
  const quote = useQuote(!fiat && w.coin && w.coin !== 'USDT' ? w.coin : '');
  const costNow = quote && D.from(quote.mark).isPos() ? D.from(w.receive).add(w.fee).mul(quote.mark).round(2, 'half_up') : null;
  const vsHeld = costNow ? D.from(w.amount).sub(costNow) : null;
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const sentOk = /^\d+(\.\d{1,2})?$/.test(amountSent.trim()) && Number(amountSent) > 0;
  // Crypto: the amount sent in the coin is optional (up to 8 decimals for BTC / ETH, 6 for USDT).
  const coinSentOk = !amountSent.trim() || (new RegExp(`^\\d+(\\.\\d{1,${w.coin === 'USDT' ? 6 : 8}})?$`).test(amountSent.trim()) && Number(amountSent) > 0);
  // The reference / hash is optional (2026-10-13); a hash, when given, must be a real one.
  const hashOk = !txHash.trim() || /^(0x)?[0-9a-fA-F]{64}$/.test(txHash.trim());
  const paidOk = fiat ? sentOk : hashOk && coinSentOk;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      await api.post(
        `/api/admin/withdrawals/${w.id}/review`,
        mode === 'reject'
          ? { decision: 'reject', reason: reason.trim() }
          : mode === 'review' || mode === 'approve'
            ? { decision: mode, ...(reason.trim() ? { reason: reason.trim() } : {}) }
          : mode === 'hold'
            ? { decision: 'hold', reason: reason.trim(), ...(holdCode.trim() ? { holdCode: holdCode.trim() } : {}) }
          : mode === 'release'
            ? { decision: 'release', reason: reason.trim(), releaseTo }
          : fiat
            ? { decision: 'paid', reference: reference.trim(), amountSent: amountSent.trim() }
            : { decision: 'paid', ...(txHash.trim() ? { txHash: txHash.trim() } : {}), ...(amountSent.trim() ? { amountSent: amountSent.trim() } : {}) },
      );
      onDone();
      onClose();
    } catch (e) {
      setMsg(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={`Withdrawal ${w.number}`} width={560}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-lg border border-line p-3 text-xs">
          <span className="text-muted">User</span>
          <span>
            {clientLabel({ name: w.name, email: w.email, crmId: w.externalClientId })}
            {w.name ? ` · ${w.email}` : ''}
          </span>
          {fiat ? (
            <>
              <span className="text-muted">Method</span>
              <span>{wdMethod(w)}</span>
              {w.payoutRows?.map(([k, v]) => (
                <Fragment key={k}>
                  <span className="text-muted">{k}</span>
                  <span className="num break-all">{v}</span>
                </Fragment>
              ))}
              <span className="text-muted">Name check</span>
              <span className={w.holderMatchesKyc ? 'text-up' : 'text-warn'}>{w.holderMatchesKyc ? 'Holder matches the verified name' : 'Holder does not match the verified name: check before paying'}</span>
              <span className="text-muted">Held</span>
              <span className="num">{fmtUsd(w.amount)}</span>
              <span className="text-muted">Fee</span>
              <span className="num">{fmtUsd(w.fee)}</span>
              <span className="text-muted">Send</span>
              <span className="num font-semibold">
                {fmtUsd(w.receive)}
                {w.currency !== 'USD' && (w.receiveEst ? ` ≈ ${CCY_SYM[w.currency]}${fmt(w.receiveEst, 2)} at ${fmt(w.rateEst!, 4)} USD/${w.currency} (client was shown)` : ` in ${w.currency}`)}
              </span>
            </>
          ) : (
            <>
              <span className="text-muted">Coin</span>
              <span>{w.coin ?? 'USDT'}</span>
              <span className="text-muted">Network</span>
              <span>{w.network}</span>
              <span className="text-muted">Address</span>
              <span className="num break-all">{w.address}</span>
              <span className="text-muted">Amount</span>
              <span className="num">
                {fmtUsd(w.amount)} → {w.coin === 'USDT' || !w.coin ? 'USDT 1:1' : w.rateEst ? `${w.coin} at ${fmtUsd(w.rateEst)} (mark when requested)` : w.coin}
              </span>
              {w.coin && w.coin !== 'USDT' && (
                <>
                  <span className="text-muted">Entered as</span>
                  <span>{w.coinFixed ? `${w.coin}: the client asked for an exact amount; held = (amount + fee) × rate` : 'USD: the coin amount is an estimate'}</span>
                </>
              )}
              <span className="text-muted">Fee</span>
              <span className="num">{coinAmt(w.fee, w.coin)}</span>
              <span className="text-muted">Send</span>
              <span className="num font-semibold">
                {w.coin === 'USDT' || !w.coin || w.coinFixed ? (w.coinFixed ? 'exactly ' : '') : '≈ '}
                {coinAmt(w.receive, w.coin)}
              </span>
              {costNow && vsHeld && (
                <>
                  <span className="text-muted">At the live rate</span>
                  <span className="num">
                    1 {w.coin} = {fmtUsd(quote!.mark)} · sending {coinAmt(w.receive, w.coin)} + fee costs {fmtUsd(costNow)} ·{' '}
                    <span className={vsHeld.isNeg() ? 'text-down' : 'text-up'}>
                      {vsHeld.isNeg() ? '−' : '+'}
                      {fmtUsd(vsHeld.abs())} vs held
                    </span>
                  </span>
                </>
              )}
            </>
          )}
          <span className="text-muted">Status</span>
          <span className={statusCls(w.status)}>
            {w.status.replace('_', ' ')}
            {w.status === 'compliance' && w.heldByName ? ` · held by ${w.heldByName}` : ''}
            {w.status === 'under_review' && w.reviewByName ? ` · by ${w.reviewByName}` : ''}
            {w.clientAction === 'verify_wallet' ? ' · client asked to verify the wallet' : ''}
          </span>
          {w.status === 'compliance' && (
            <>
              <span className="text-muted">Hold</span>
              <span className="text-warn">
                {w.holdCode ? `${w.holdCode}: ` : ''}
                {w.holdReason}
              </span>
            </>
          )}
          {!fiat && (
            <>
              <span className="text-muted">Wallet</span>
              <span>
                <button type="button" className="text-accent-fg underline-offset-2 hover:underline" onClick={() => setWalletOpen(true)}>
                  Open the wallet card
                </button>
              </span>
            </>
          )}
        </div>
        <div role="group" className="flex w-fit flex-wrap rounded-full border border-line bg-panel p-[3px]">
          {modes.map((m) => (
            <button key={m} type="button" aria-pressed={mode === m} onClick={() => setMode(m)} className={cx('h-[26px] rounded-full px-3 text-xs font-semibold', mode === m ? 'bg-raised text-fg' : 'text-muted')}>
              {WD_MODE_LABEL[m]}
            </button>
          ))}
        </div>
        {mode === 'paid' && fiat ? (
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-[11px] text-muted">
              Amount sent ({w.currency})
              <input required value={amountSent} onChange={(e) => setAmountSent(e.target.value)} inputMode="decimal" className={cx(input, 'num')} />
              <span>What the client receives, shown to them as final</span>
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-muted">
              {w.method === 'card' ? 'Card processor reference (optional)' : 'Bank payment reference (optional)'}
              <input value={reference} maxLength={100} onChange={(e) => setReference(e.target.value)} className={cx(input, 'num')} />
            </label>
          </div>
        ) : mode === 'paid' ? (
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-[11px] text-muted">
              Transaction hash (optional)
              <input value={txHash} onChange={(e) => setTxHash(e.target.value)} placeholder="64 hex characters, 0x optional" className={cx(input, 'num')} />
              {txHash && !/^(0x)?[0-9a-fA-F]{64}$/.test(txHash.trim()) && <span className="text-down">Must be 64 hex characters (optionally prefixed with 0x)</span>}
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-muted">
              Amount sent ({w.coin ?? 'USDT'}{w.coinFixed ? '' : ', optional'})
              <input value={amountSent} onChange={(e) => setAmountSent(e.target.value)} inputMode="decimal" placeholder={D.from(w.receive).toString()} className={cx(input, 'num')} />
              {!coinSentOk && <span className="text-down">A positive amount in {w.coin ?? 'USDT'}</span>}
              {coinSentOk && w.coinFixed && amountSent.trim() && D.from(amountSent.trim()).cmp(w.receive) !== 0 && (
                <span className="text-warn">Not what the client asked for ({coinAmt(w.receive, w.coin)}); their ticket will show what you enter</span>
              )}
            </label>
          </div>
        ) : mode === 'reject' ? (
          <label className="flex flex-col gap-1 text-[11px] text-muted">
            Reason (shown to the user)
            <textarea required rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className={textarea} />
            <span>Rejecting returns {fmtUsd(w.amount)} to the user's balance.</span>
          </label>
        ) : mode === 'hold' ? (
          <div className="grid grid-cols-[minmax(0,1fr)_160px] gap-3">
            <label className="flex flex-col gap-1 text-[11px] text-muted">
              Reason (optional, staff only, never shown to the client)
              <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className={textarea} />
              <span>The client sees “Under review” and can no longer cancel. It can't be paid until released.</span>
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-muted">
              Code (optional)
              <input value={holdCode} onChange={(e) => setHoldCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} placeholder="manual" className={cx(input, 'num')} />
            </label>
          </div>
        ) : mode === 'release' ? (
          <div className="grid grid-cols-[minmax(0,1fr)_180px] gap-3">
            <label className="flex flex-col gap-1 text-[11px] text-muted">
              Note (optional)
              <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className={textarea} />
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-muted">
              Release to
              <select value={releaseTo} onChange={(e) => setReleaseTo(e.target.value as 'under_review' | 'approved')} className={input}>
                <option value="under_review">Under review</option>
                <option value="approved">Approved (Processing)</option>
              </select>
            </label>
          </div>
        ) : (
          <label className="flex flex-col gap-1 text-[11px] text-muted">
            Note (optional)
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className={textarea} />
            <span>{mode === 'review' ? 'The client sees “Under review” and can still cancel.' : 'The client sees “Processing”; the amount stays held until you mark it paid.'}</span>
          </label>
        )}
        <Err msg={msg} />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btn}>
            Cancel
          </button>
          {mode === 'paid' ? (
            <button type="submit" disabled={busy || !paidOk} className={btnPrimary}>
              {busy ? <Spinner /> : 'Mark as paid'}
            </button>
          ) : mode === 'reject' ? (
            <button type="submit" disabled={busy || !reason.trim()} className={btnDanger}>
              {busy ? <Spinner /> : 'Reject and refund'}
            </button>
          ) : (
            <button type="submit" disabled={busy} className={btnPrimary}>
              {busy ? <Spinner /> : WD_MODE_LABEL[mode]}
            </button>
          )}
        </div>
      </form>
      {walletOpen && (
        <Modal open onClose={() => setWalletOpen(false)} title="Wallet card" width={880}>
          <WalletCards userId={w.userId} focusAddress={w.address ?? undefined} />
        </Modal>
      )}
    </Modal>
  );
}

export function WithdrawalsTab() {
  const [status, setStatus] = useState<WdFilter>('pending');
  const [open, setOpen] = useState<AdminWithdrawal | null>(null);
  const { data, error, loading, reload } = useLoad<AdminWithdrawal[]>(`/api/admin/withdrawals${status ? `?status=${status}` : ''}`);
  return (
    <div className="flex flex-col gap-3">
      <Filter
        value={status}
        onChange={setStatus}
        options={[
          { id: 'pending', label: 'Pending' },
          { id: 'under_review', label: 'Under review' },
          { id: 'compliance', label: 'Compliance hold' },
          { id: 'approved', label: 'Processing' },
          { id: 'paid', label: 'Paid' },
          { id: 'rejected', label: 'Rejected' },
          { id: 'cancelled', label: 'Cancelled' },
          { id: '', label: 'All' },
        ]}
      />
      <Err msg={error} />
      <Table head={['#', 'Created', 'Client', 'Method', 'To', 'Amount', 'Entered as', 'Fee', 'Receive', 'Status', '']} minWidth={1180}>
        {data?.map((w) => (
          <tr key={w.id} className="num border-b border-line">
            <td className={cx(td, 'font-semibold')}>{w.number}</td>
            <td className={cx(td, 'text-muted')}>{when(w.createdAt)}</td>
            <td className={td}>
              <ClientCell name={w.name} email={w.email} crmId={w.externalClientId} />
            </td>
            <td className={cx(td, 'font-sans')}>{wdMethod(w)}</td>
            <td className={cx(td, 'max-w-[180px] truncate')} title={w.destination}>
              {w.destination}
            </td>
            <td className={td}>
              {fmt(w.amount, 2)}
              {w.method === 'crypto' && w.coin && w.coin !== 'USDT' && w.rateEst && <div className="text-[11px] text-muted">@ {fmtUsd(w.rateEst)}</div>}
            </td>
            <td className={cx(td, 'font-sans')}>{w.method === 'crypto' && w.coin && w.coin !== 'USDT' && w.coinFixed ? <span className="font-semibold text-warn">{w.coin}</span> : 'USD'}</td>
            <td className={cx(td, 'text-muted')}>{w.method === 'crypto' && w.coin && w.coin !== 'USDT' ? coinAmt(w.fee, w.coin) : fmt(w.fee, 2)}</td>
            <td className={cx(td, 'font-semibold')}>
              {w.method === 'crypto' && w.coin && w.coin !== 'USDT' ? coinAmt(w.amountSent ?? w.receive, w.coin) : fmt(w.receive, 2)}
              {w.method === 'crypto' && w.coin && w.coin !== 'USDT' && !w.amountSent && (
                <div className={cx('font-sans text-[11px] font-normal', w.coinFixed ? 'text-warn' : 'text-muted')}>{w.coinFixed ? 'exact amount' : 'estimate'}</div>
              )}
              {w.method !== 'crypto' && w.currency !== 'USD' && (w.amountSent ?? w.receiveEst) && (
                <div className="text-[11px] font-normal text-muted">
                  {w.amountSent ? '' : '≈ '}
                  {CCY_SYM[w.currency]}
                  {fmt((w.amountSent ?? w.receiveEst)!, 2)}
                </div>
              )}
            </td>
            <td className={cx(td, 'font-sans', statusCls(w.status))} title={w.holdReason ?? w.rejectReason ?? w.txHash ?? w.reference ?? undefined}>
              {w.status.replace('_', ' ')}
              {w.clientAction === 'verify_wallet' && <div className="text-[11px] text-warn">wallet check</div>}
            </td>
            <td className={td}>
              {WD_MODES[w.status] ? (
                <button type="button" className={btn} onClick={() => setOpen(w)}>
                  Process
                </button>
              ) : w.txHash ? (
                <span className="text-muted" title={w.txHash}>
                  {short(w.txHash)}
                </span>
              ) : w.reference ? (
                <span className="text-muted" title={w.reference}>
                  {w.reference}
                </span>
              ) : null}
            </td>
          </tr>
        ))}
      </Table>
      {loading && !data && <Spinner />}
      {data && !data.length && <Empty>No {status || ''} withdrawals</Empty>}
      {open && <WithdrawalReview w={open} onClose={() => setOpen(null)} onDone={() => void reload()} />}
    </div>
  );
}

// ---------- Deposits ----------

type DepFilter = 'pending' | 'credited' | 'rejected' | '';
type AdminDeposit = Deposit & { userId: string; email: string; name: string | null; externalClientId: string | null; address: string | null };
interface PoolStat {
  network: Network;
  free: number;
  assigned: number;
}

function ImportAddresses({ onDone }: { onDone: () => void }) {
  const [network, setNetwork] = useState<Network>('TRC20');
  const [addresses, setAddresses] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post<{ added: number; skipped: number }>('/api/admin/deposit-addresses', { network, addresses });
      setMsg({ ok: true, text: `Added ${r.added}, skipped ${r.skipped} (already in the pool).` });
      setAddresses('');
      onDone();
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-2.5 rounded-lg border border-line bg-panel p-3">
      <h3 className="m-0 text-sm font-semibold">Import addresses</h3>
      <div className="flex flex-wrap items-start gap-2">
        <select value={network} onChange={(e) => setNetwork(e.target.value as Network)} className={input} aria-label="Network">
          {NETWORKS.map((n) => (
            <option key={n.id} value={n.id}>
              {n.id} · {n.chain}
            </option>
          ))}
        </select>
        <textarea required rows={3} value={addresses} onChange={(e) => setAddresses(e.target.value)} placeholder="One address per line (or comma separated)" className={cx(textarea, 'num min-w-[260px] flex-1')} aria-label="Addresses" />
      </div>
      <div className="flex items-center gap-3">
        <button type="submit" disabled={busy || !addresses.trim()} className={btnPrimary}>
          {busy ? <Spinner /> : 'Import'}
        </button>
        {msg && <span className={cx('text-xs whitespace-pre-wrap', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</span>}
      </div>
    </form>
  );
}

export function DepositsTab() {
  const [status, setStatus] = useState<DepFilter>('');
  const pool = useLoad<PoolStat[]>('/api/admin/deposit-addresses');
  const { data, error, loading } = useLoad<AdminDeposit[]>(`/api/admin/deposits${status ? `?status=${status}` : ''}`);
  return (
    <div className="flex flex-col gap-3">
      <Err msg={pool.error} />
      <div className="grid gap-3 sm:grid-cols-3">
        {pool.data?.map((p) => (
          <div key={p.network} className={cx('rounded-lg border p-3 text-xs', p.free === 0 ? 'border-warn bg-warn-bg' : 'border-line bg-panel')}>
            <div className="mb-1 font-semibold">{p.network}</div>
            <div className="num grid grid-cols-2 gap-1">
              <span className="font-sans text-muted">Free addresses</span>
              <span className={cx('font-semibold', p.free === 0 && 'text-warn')}>{p.free}</span>
              <span className="font-sans text-muted">Assigned</span>
              <span>{p.assigned}</span>
            </div>
            {p.free === 0 && <p className="m-0 mt-2 text-[11.5px] text-warn">Users can't get a {p.network} address until you import more.</p>}
          </div>
        ))}
      </div>
      <ImportAddresses onDone={() => void pool.reload()} />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <Filter
          value={status}
          onChange={setStatus}
          options={[
            { id: '', label: 'All' },
            { id: 'pending', label: 'Pending' },
            { id: 'credited', label: 'Credited' },
            { id: 'rejected', label: 'Rejected' },
          ]}
        />
      </div>
      <Err msg={error} />
      <Table head={['Created', 'Client', 'Network', 'Address', 'Tx hash', 'Amount', 'Confirmations', 'Status']} minWidth={1000}>
        {data?.map((d) => (
          <tr key={d.id} className="num border-b border-line">
            <td className={cx(td, 'text-muted')}>{when(d.createdAt)}</td>
            <td className={td}>
              <ClientCell name={d.name} email={d.email} crmId={d.externalClientId} />
            </td>
            <td className={cx(td, 'font-sans')}>{d.network}</td>
            <td className={cx(td, 'max-w-[180px] truncate')} title={d.address ?? undefined}>
              {d.address ?? '—'}
            </td>
            <td className={td} title={d.txHash ?? undefined}>
              {d.source === 'manual' ? <span className="font-sans text-muted">manual</span> : short(d.txHash)}
            </td>
            <td className={cx(td, 'font-semibold')}>{fmt(d.amount, 2)}</td>
            <td className={cx(td, d.confirmations < d.requiredConfirmations && 'text-warn')}>
              {d.confirmations} / {d.requiredConfirmations}
            </td>
            <td className={cx(td, 'font-sans', statusCls(d.status))}>{d.status}</td>
          </tr>
        ))}
      </Table>
      {loading && !data && <Spinner />}
      {data && !data.length && <Empty>No {status || ''} deposits</Empty>}
      <p className="m-0 text-[11.5px] text-muted">On-chain deposits are reported by your payment processor via POST /api/crm/deposits. To book a deposit by hand, use Users → credit.</p>
    </div>
  );
}

// ---------- Support ----------

type TicketFilter = TicketStatus | '';
type AdminTicket = Ticket & { userId: string; email: string; name: string | null; externalClientId: string | null };
type AdminTicketDetail = TicketDetail & { userId: string; email: string; name: string | null; externalClientId: string | null };

const TICKET_STATUS: { id: TicketStatus; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'awaiting_user', label: 'Awaiting user' },
  { id: 'resolved', label: 'Resolved' },
];
const ticketStatusLabel = (s: TicketStatus) => TICKET_STATUS.find((x) => x.id === s)?.label ?? s;
const ticketStatusCls = (s: TicketStatus) => (s === 'open' ? 'text-warn' : s === 'resolved' ? 'text-up' : 'text-muted');
const categoryLabel = ticketCategoryLabel;

function TicketView({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { data: t, error, reload } = useLoad<AdminTicketDetail>(`/api/admin/tickets/${id}`);
  const [message, setMessage] = useState('');
  const [nextStatus, setNextStatus] = useState<TicketStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const send = async (body: { message?: string; status?: TicketStatus }) => {
    setBusy(true);
    setMsg(null);
    setConflict(false);
    try {
      await api.post(`/api/admin/tickets/${id}/reply`, body);
      if (body.message) setMessage('');
      setNextStatus(null);
      await reload();
      onChanged();
    } catch (e) {
      setMsg(errMsg(e));
      if (e instanceof ApiError && e.status === 409) setConflict(true);
    } finally {
      setBusy(false);
    }
  };
  const download = (a: { id: string; filename: string }) => downloadFile(`/api/admin/tickets/attachments/${a.id}`, a.filename).catch((e) => setMsg(errMsg(e, 'Download failed')));
  const text = message.trim();
  return (
    <Modal open onClose={onClose} title={t ? `${t.number} · ${t.subject}` : 'Ticket'} width={720}>
      <Err msg={error} />
      {!t ? (
        !error && <Spinner />
      ) : (
        <div className="flex max-h-[75vh] flex-col gap-4 overflow-y-auto">
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-lg border border-line p-3 text-xs">
            <span className="text-muted">Status</span>
            <span className={cx('font-semibold', ticketStatusCls(t.status))}>{ticketStatusLabel(t.status)}</span>
            <span className="text-muted">Topic</span>
            <span>{categoryLabel(t.category)}</span>
            <span className="text-muted">Client</span>
            <span>
              {clientLabel({ name: t.name, email: t.email, crmId: t.externalClientId })}
              {t.name ? ` · ${t.email}` : ''}
            </span>
            {t.reference && (
              <>
                <span className="text-muted">Reference</span>
                <span className="num">{t.reference}</span>
              </>
            )}
            {t.category === 'callback' && (
              <>
                <span className="text-muted">Phone</span>
                <span className="num">{t.phone ?? '—'}</span>
                <span className="text-muted">Call back</span>
                <span>{t.callbackWindow ?? '—'}</span>
              </>
            )}
            <span className="text-muted">Opened</span>
            <span className="num">{when(t.createdAt)}</span>
          </div>
          <div className="flex flex-col gap-2.5">
            {t.messages.map((m) => (
              <div key={m.id} className={cx('flex max-w-[85%] flex-col gap-1', m.author === 'staff' ? 'self-end items-end' : 'self-start items-start')}>
                <div className="text-[11px] text-muted">
                  {m.authorName} · <span className="num">{when(m.createdAt)}</span>
                </div>
                <div className={cx('rounded-lg px-3 py-2 text-[13px] whitespace-pre-wrap break-words', m.author === 'staff' ? 'bg-accent-bg text-fg' : 'border border-line bg-raised')}>{m.body}</div>
                {m.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {m.attachments.map((a) => (
                      <button key={a.id} type="button" className={cx(btn, 'h-7 font-medium')} onClick={() => void download(a)} title={`Download ${a.filename}`}>
                        {a.filename} <span className="num text-muted">({bytes(a.size)})</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-2 border-t border-line pt-3">
            {t.status === 'resolved' && (
              <div className="flex items-center gap-3 text-xs text-muted">
                This ticket is resolved. Reopen it to continue the conversation.
                <button type="button" disabled={busy} className={btn} onClick={() => void send({ status: 'open' })}>
                  Reopen
                </button>
              </div>
            )}
            <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Write a reply to the user" className={textarea} aria-label="Reply" />
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" disabled={busy || !text} className={btnPrimary} onClick={() => void send({ message: text })}>
                {busy ? <Spinner /> : 'Reply'}
              </button>
              <button type="button" disabled={busy || !text} className={btn} onClick={() => void send({ message: text, status: 'resolved' })}>
                Reply and resolve
              </button>
              <div className="flex-1" />
              <select value={nextStatus ?? t.status} onChange={(e) => setNextStatus(e.target.value as TicketStatus)} className={cx(input, 'h-8 text-xs')} aria-label="Status">
                {TICKET_STATUS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
              <button type="button" disabled={busy || !nextStatus || nextStatus === t.status} className={btn} onClick={() => nextStatus && void send({ status: nextStatus })}>
                Set status
              </button>
            </div>
            <p className="m-0 text-[11.5px] text-muted">Reply sets the ticket to Awaiting user.</p>
            <Err msg={msg} />
            {conflict && (
              <div>
                <button type="button" disabled={busy} className={btn} onClick={() => void send({ status: 'open' })}>
                  Reopen
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

export function SupportTab() {
  const [status, setStatus] = useState<TicketFilter>('open');
  const [open, setOpen] = useState<string | null>(null);
  const { data, error, loading, reload } = useLoad<AdminTicket[]>(`/api/admin/tickets${status ? `?status=${status}` : ''}`);
  return (
    <div className="flex flex-col gap-3">
      <Filter value={status} onChange={setStatus} options={[...TICKET_STATUS, { id: '' as const, label: 'All' }]} />
      <Err msg={error} />
      <Table head={['#', 'Updated', 'Client', 'Topic', 'Subject', 'Status']}>
        {data?.map((t) => (
          <tr key={t.id} onClick={() => setOpen(t.id)} className="cursor-pointer border-b border-line hover:bg-raised">
            <td className={cx(td, 'num font-semibold')}>
              <button type="button" className="text-left text-fg" onClick={() => setOpen(t.id)}>
                {t.number}
              </button>
            </td>
            <td className={cx(td, 'num text-muted')}>{when(t.updatedAt)}</td>
            <td className={td}>
              <ClientCell name={t.name} email={t.email} crmId={t.externalClientId} />
            </td>
            <td className={cx(td, 'text-muted')}>{categoryLabel(t.category)}</td>
            <td className={cx(td, 'max-w-[320px] truncate')} title={t.subject}>
              {t.subject}
            </td>
            <td className={cx(td, ticketStatusCls(t.status))}>{ticketStatusLabel(t.status)}</td>
          </tr>
        ))}
      </Table>
      {loading && !data && <Spinner />}
      {data && !data.length && <Empty>No {status ? ticketStatusLabel(status).toLowerCase() : ''} tickets</Empty>}
      {open && <TicketView id={open} onClose={() => setOpen(null)} onChanged={() => void reload()} />}
    </div>
  );
}

// ---------- Compliance roll-out (one-time) ----------

type RolloutAction = 'flag' | 'already_flagged' | 'under_review' | 'approved';
interface RolloutRow {
  userId: string;
  email: string;
  name: string;
  externalClientId: string | null;
  lifetimeDeposits: string;
  kycStatus: string;
  sofStatus: string;
  poaIssuedOn: string | null;
  action: RolloutAction;
  dueAt: string | null;
}
interface Rollout {
  threshold: string;
  deadlineDays: number;
  enforced: boolean;
  enforcedBy: 'env' | 'admin' | null;
  enforcedSince: string | null;
  enforcedByName: string | null;
  counts: Record<RolloutAction, number>;
  rows: RolloutRow[];
  flagged?: number;
}

const ROLLOUT_ACTION: Record<RolloutAction, [string, string]> = {
  flag: ['Will be flagged', 'text-warn'],
  already_flagged: ['Already flagged', 'text-muted'],
  under_review: ['Under review', 'text-accent'],
  approved: ['Approved', 'text-up'],
};

function rolloutCsv(rows: RolloutRow[]) {
  const cell = (v: string | null) => (v == null ? '' : /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const head = 'user_id,email,name,lifetime_deposits_usd,kyc_status,sof_status,poa_issued_on,action,due_at,crm_id';
  return [head, ...rows.map((r) => [r.userId, r.email, r.name, r.lifetimeDeposits, r.kycStatus, r.sofStatus, r.poaIssuedOn, r.action, r.dueAt, r.externalClientId].map(cell).join(','))].join('\n') + '\n';
}

/**
 * Clients whose deposits were already past the threshold before the source-of-funds rule: preview
 * who would be asked, then flag them (email + notice, SOF_BACKFILL_DAYS to respond). Re-running only
 * reaches clients not flagged yet.
 */
export function RolloutTab() {
  const { data: fetched, error, loading, reload } = useLoad<Rollout>('/api/admin/compliance/rollout');
  const [result, setResult] = useState<Rollout | null>(null);
  const [checked, setChecked] = useState(false);
  const [enforceAfter, setEnforceAfter] = useState(true);
  const [enforceChecked, setEnforceChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const data = result ?? fetched;
  const toFlag = fetched?.counts.flag ?? 0;
  // After flagging, the rows marked "flag" were just flagged.
  const actionLabel = (a: RolloutAction) => (a === 'flag' && result ? 'Flagged now' : ROLLOUT_ACTION[a][0]);

  const apply = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post<Rollout>('/api/admin/compliance/rollout', { expect: toFlag, enforce: enforceAfter });
      setResult(r);
      setChecked(false);
      void reload();
    } catch (e) {
      setMsg(errMsg(e));
      if (e instanceof ApiError && e.code === 'preview_changed') void reload();
    } finally {
      setBusy(false);
    }
  };
  const switchEnforcement = async (on: boolean) => {
    setBusy(true);
    setMsg(null);
    try {
      setResult(null);
      await api.post<Rollout>('/api/admin/compliance/enforce', { on });
      setEnforceChecked(false);
      void reload();
    } catch (e) {
      setMsg(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const download = () => {
    if (!data) return;
    const url = URL.createObjectURL(new Blob([rolloutCsv(data.rows)], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `over-threshold-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="m-0 text-base font-semibold">Source-of-funds roll-out</h2>
        <p className="m-0 max-w-3xl text-xs text-muted">
          Clients whose live deposits already total more than {data ? fmtUsd(data.threshold, 0) : 'the threshold'}. Flagging asks each of them for 3 months of bank statements within{' '}
          {data?.deadlineDays ?? 30} days, by email and in-app notice. Clients already flagged, under review or approved are left alone, so this can be run again later.
        </p>
      </div>
      <Err msg={error} />
      {loading && !data && <Spinner />}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {(Object.keys(ROLLOUT_ACTION) as RolloutAction[]).map((a) => (
              <div key={a} className="rounded-lg border border-line bg-panel p-3">
                <div className="text-[11px] text-muted">{actionLabel(a)}</div>
                <div className={cx('num text-xl font-semibold', a === 'flag' && data.counts.flag ? 'text-warn' : '')}>{data.counts[a]}</div>
              </div>
            ))}
          </div>

          {result?.flagged !== undefined && (
            <p className="m-0 rounded-lg bg-up-bg px-3 py-2 text-xs text-up" role="status">
              Flagged {result.flagged} client{result.flagged === 1 ? '' : 's'}. They have been emailed; their deadlines are in the table. Download the CSV for your records.
            </p>
          )}
          {data.enforced ? (
            <div className="flex flex-wrap items-center gap-3 rounded-lg bg-up-bg px-3 py-2 text-xs text-up">
              <span>
                <b>Enforcement is on.</b>{' '}
                {data.enforcedBy === 'env'
                  ? 'Set by COMPLIANCE_ENFORCE=true on the server.'
                  : `Switched on ${data.enforcedSince ? when(data.enforcedSince) : ''}${data.enforcedByName ? ` by ${data.enforcedByName}` : ''}.`}{' '}
                Deposits past the threshold, withdrawals and missed deadlines now follow the rules.
              </span>
              {data.enforcedBy === 'admin' && (
                <button type="button" className={cx(btn, 'ml-auto')} disabled={busy} onClick={() => void switchEnforcement(false)}>
                  Turn off
                </button>
              )}
            </div>
          ) : (
            <p className="m-0 rounded-lg bg-raised px-3 py-2 text-xs text-muted">
              <b className="text-fg">Enforcement is off.</b> Flagged clients are asked for documents, but nothing is refused or restricted yet. It can be switched on once every client past the
              threshold has been flagged.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={btn} onClick={download} disabled={!data.rows.length}>
              Download CSV
            </button>
            <button type="button" className={btn} onClick={() => { setResult(null); void reload(); }}>
              Refresh preview
            </button>
          </div>

          <Table head={['Client', 'Lifetime deposits', 'KYC', 'Source of funds', 'Proof of address', 'Action', 'Deadline']} minWidth={960}>
            {data.rows.map((r) => (
              <tr key={r.userId} className="border-b border-line">
                <td className={td}>
                  <ClientCell name={r.name} email={r.email} crmId={r.externalClientId} />
                </td>
                <td className={cx(td, 'num')}>{fmtUsd(r.lifetimeDeposits)}</td>
                <td className={cx(td, statusCls(r.kycStatus))}>{r.kycStatus}</td>
                <td className={td}>{r.sofStatus.replace('_', ' ')}</td>
                <td className={cx(td, 'num text-muted')}>{r.poaIssuedOn ?? '—'}</td>
                <td className={cx(td, ROLLOUT_ACTION[r.action][1])}>{actionLabel(r.action)}</td>
                <td className={cx(td, 'num text-muted')}>{r.dueAt ? when(r.dueAt) : '—'}</td>
              </tr>
            ))}
          </Table>
          {!data.rows.length && <Empty>No client is past {fmtUsd(data.threshold, 0)} in deposits</Empty>}

          {toFlag === 0 && !data.enforced && (
            <div className="flex flex-col gap-2.5 rounded-lg border border-line p-3">
              <h3 className="m-0 text-sm font-semibold">Turn on enforcement</h3>
              <p className="m-0 text-xs text-muted">Every client past {fmtUsd(data.threshold, 0)} has been flagged, is under review or is approved.</p>
              <label className="flex cursor-pointer items-start gap-2 text-xs">
                <input type="checkbox" checked={enforceChecked} onChange={(e) => setEnforceChecked(e.target.checked)} className="mt-0.5" />
                Refuse deposits past the threshold without an approved source of funds, pause withdrawals of flagged clients, expire IDs and set close-only after missed deadlines.
              </label>
              <div>
                <button type="button" disabled={busy || !enforceChecked} className={btnPrimary} onClick={() => void switchEnforcement(true)}>
                  {busy ? <Spinner /> : 'Turn on enforcement'}
                </button>
              </div>
              <Err msg={msg} />
            </div>
          )}

          {toFlag > 0 && (
            <div className="flex flex-col gap-2.5 rounded-lg border border-line p-3">
              <h3 className="m-0 text-sm font-semibold">Flag {toFlag} client{toFlag === 1 ? '' : 's'}</h3>
              <label className="flex cursor-pointer items-start gap-2 text-xs">
                <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="mt-0.5" />
                I checked the list. Email {toFlag === 1 ? 'this client' : `these ${toFlag} clients`} and give {toFlag === 1 ? 'them' : 'each of them'} {fetched?.deadlineDays ?? 30} days to send their source of funds. This can't be undone.
              </label>
              {!data.enforced && (
                <label className="flex cursor-pointer items-start gap-2 text-xs">
                  <input type="checkbox" checked={enforceAfter} onChange={(e) => setEnforceAfter(e.target.checked)} className="mt-0.5" />
                  Then turn on enforcement: refuse deposits past the threshold without an approved source of funds, pause withdrawals of flagged clients, and set close-only after missed deadlines.
                </label>
              )}
              <div>
                <button type="button" disabled={busy || !checked} className={btnPrimary} onClick={() => void apply()}>
                  {busy ? <Spinner /> : `Flag ${toFlag} client${toFlag === 1 ? '' : 's'}`}
                </button>
              </div>
              <Err msg={msg} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
